import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import { Visibility, Add } from '@mui/icons-material';
import { StockTransferService } from '../../services/stockTransfer.service';
import {
    StockTransferDTO,
    StockTransferStatus,
    STOCK_TRANSFER_STATUS_LABELS,
} from '../../interfaces/stockTransfer.interface';

const PAGE_SIZE = 10;

const STATUS_OPTIONS: StockTransferStatus[] = [
    'DRAFT',
    'PENDING_APPROVAL',
    'COMPLETED',
    'CANCELLED',
];

const STATUS_COLORS: Record<StockTransferStatus, 'default' | 'success' | 'warning' | 'error'> = {
    DRAFT: 'default',
    PENDING_APPROVAL: 'warning',
    COMPLETED: 'success',
    CANCELLED: 'error',
};

/** Newest first: transfer date, then reference number (both descending). */
export const sortTransfersNewestFirst = (list: StockTransferDTO[]): StockTransferDTO[] =>
    [...list].sort((a, b) => {
        const d = (b.transferDate ?? '').localeCompare(a.transferDate ?? '');
        return d !== 0 ? d : (b.referenceNo ?? '').localeCompare(a.referenceNo ?? '', undefined, { numeric: true });
    });

const StockTransferList: React.FC = () => {
    const navigate = useNavigate();
    const [transfers, setTransfers] = useState<StockTransferDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0,
    });

    const handleViewDetails = (id: string) => {
        navigate(`/stock-transfers/${id}`);
    };

    const fetchTransfers = async (page: number = 0, status: string = statusFilter) => {
        try {
            setLoading(true);
            const response = await StockTransferService.getStockTransfersByInstitution(status, page);
            setTransfers(sortTransfersNewestFirst(response.content));
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements,
            });
            setError('');
        } catch (err) {
            setError('Failed to load stock transfers');
            console.error('Error fetching stock transfers:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransfers(0, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        setStatusFilter(event.target.value);
    };

    const handlePageChange = (_event: unknown, page: number) => {
        fetchTransfers(page, statusFilter);
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="store.transfers.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Stock Transfers</Typography>
                <Button
                    data-help="store.transfers.new"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/stock-transfers/create')}
                >
                    New Transfer
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.transfers.filter">
                <FormControl size="small" sx={{ flex: '1 1 220px', maxWidth: 260 }}>
                    <InputLabel>Status</InputLabel>
                    <Select data-help="store.transfers.filter.status" value={statusFilter} label="Status" onChange={handleStatusChange}>
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>
                                {STOCK_TRANSFER_STATUS_LABELS[status]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    Total: {pageInfo.totalElements}
                </Typography>
            </Box>

            {loading ? (
                <div data-testid="loading-state">Loading...</div>
            ) : (
                <TableContainer component={Paper} data-help-section="store.transfers.table">
                    <Table size="small" data-testid="stock-transfers-table" sx={{ minWidth: 640 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                                <TableCell data-help="store.transfers.col.refNo">Reference No</TableCell>
                                <TableCell data-help="store.transfers.col.from">From Store</TableCell>
                                <TableCell data-help="store.transfers.col.to">To Store</TableCell>
                                <TableCell data-help="store.transfers.col.date">Date</TableCell>
                                <TableCell data-help="store.transfers.col.status">Status</TableCell>
                                <TableCell data-help="store.transfers.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transfers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No stock transfers found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transfers.map((transfer) => (
                                    <TableRow key={transfer.id} hover data-testid={`stock-transfer-row-${transfer.id}`}>
                                        <TableCell>{transfer.referenceNo}</TableCell>
                                        <TableCell>{transfer.fromStoreName || '-'}</TableCell>
                                        <TableCell>{transfer.toStoreName || '-'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {transfer.transferDate ? formatDate(transfer.transferDate) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={STATUS_COLORS[transfer.status] ?? 'warning'}
                                                label={STOCK_TRANSFER_STATUS_LABELS[transfer.status] ?? transfer.status}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(transfer.id)}
                                                    color="primary"
                                                    data-testid={`view-button-${transfer.id}`}
                                                    data-help="store.transfers.action.view"
                                                    aria-label="View Details"
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={pageInfo.totalElements}
                        page={pageInfo.currentPage}
                        onPageChange={handlePageChange}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                    />
                </TableContainer>
            )}

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </Box>
    );
};

export default StockTransferList;
